/**
 * Tailored Canvases — "supply the details we asked for" page.
 *
 * Opened from the Request-details email:
 *   /pages/upload-details?order-id=<id>&request=<requestId>&token=<token>
 *
 * Three parameters, not four: the app does not put `shop` in the link. This page
 * resolves it itself — an explicit ?shop= wins, then the section's dev override,
 * then the store the page is being served from.
 *
 * The link carries only those four identifiers. What is actually being asked
 * for — the message, how many photos, which named values, which canvases — is
 * fetched from the app, because a URL is editable by whoever holds it and an
 * email is a snapshot of a request that may since have been answered.
 *
 * Photos are posted to the app, which stores them and returns the public URL.
 * They used to go straight to storage with a presigned PUT — fewer hops, but it
 * left the app unable to cap the size, check that the file is really an image,
 * or take the GPS coordinates out of a photo taken at home.
 *
 * Written in the same conservative style as image-check.js (var, function,
 * .then) — the theme has no build step.
 */
document.addEventListener('DOMContentLoaded', function () {
  var root = document.querySelector('[data-upload-details]');
  if (!root) return;

  var API_BASE = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
  var DETAILS_ENDPOINT = '/api/customer-data-request';
  var UPLOAD_ENDPOINT = '/api/customer-data-request/upload';

  var params = new URLSearchParams(window.location.search);
  var orderId = params.get('order-id') || params.get('orderId');
  var requestId = params.get('request');
  var token = params.get('token') || '';
  // Same precedence as image-check: explicit ?shop= → dev override → this store.
  var shop =
    params.get('shop') ||
    root.getAttribute('data-shop-override') ||
    root.getAttribute('data-shop') ||
    '';

  var loadingEl = root.querySelector('[data-ud-loading]');
  var submittedEl = root.querySelector('[data-ud-submitted]');
  var expiredEl = root.querySelector('[data-ud-expired]');
  var errorEl = root.querySelector('[data-ud-error]');
  var errorTextEl = root.querySelector('[data-ud-error-text]');
  var successEl = root.querySelector('[data-ud-success]');
  var formEl = root.querySelector('[data-ud-form]');
  var messageEl = root.querySelector('[data-ud-message]');
  var itemsEl = root.querySelector('[data-ud-items]');
  var formErrorEl = root.querySelector('[data-ud-form-error]');
  var submitEl = root.querySelector('[data-ud-submit]');
  var orderNumberEl = root.querySelector('[data-ud-order-number]');

  var itemTemplate = root.querySelector('[data-ud-item-template]');
  var slotTemplate = root.querySelector('[data-ud-slot-template]');
  var fieldTemplate = root.querySelector('[data-ud-field-template]');

  var config = null; // the GET response
  var state = []; // [{ lineItemId, photos: [{url|null,...}], values: {key: input} }]
  var sending = false;

  function show(el) {
    if (el) el.hidden = false;
  }
  function hide(el) {
    if (el) el.hidden = true;
  }

  /** Only ever one state on screen. */
  function showOnly(el) {
    [loadingEl, submittedEl, expiredEl, errorEl, successEl, formEl].forEach(hide);
    show(el);
  }

  function fail(message) {
    if (message && errorTextEl) errorTextEl.textContent = message;
    showOnly(errorEl);
  }

  function query() {
    var q = '?order-id=' + encodeURIComponent(orderId);
    q += '&request=' + encodeURIComponent(requestId);
    if (token) q += '&token=' + encodeURIComponent(token);
    if (shop) q += '&shop=' + encodeURIComponent(shop);
    return q;
  }

  function identifiers() {
    var body = { orderId: orderId, request: requestId, token: token };
    if (shop) body.shop = shop;
    return body;
  }

  // ---- photo upload ------------------------------------------------------

  /**
   * Send one photo to the app, which stores it and hands back its public URL.
   *
   * XMLHttpRequest rather than fetch, and this is the only reason: only XHR
   * reports upload progress. On a 15MB photo over a phone connection that is
   * the difference between a progress bar and an apparently frozen page.
   *
   * The identifiers travel in the query string because the body is the file
   * itself — no multipart wrapper, nothing for the server to unpick before it
   * can start counting bytes.
   */
  function uploadPhoto(file, lineItemId, onProgress) {
    var query = new URLSearchParams({
      'order-id': orderId || '',
      request: requestId || '',
      token: token,
      // Which canvas this photo answers for. Each canvas carries its own slot
      // count now, so without it the app could only ask whether ANY canvas
      // wants photos — and a photo could land against one that asked for none.
      'line-item-id': lineItemId || '',
    });
    if (shop) query.set('shop', shop);

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', API_BASE + UPLOAD_ENDPOINT + '?' + query.toString(), true);
      // The app checks this against what the first bytes of the file actually
      // say, so a wrong value here is refused rather than believed.
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.onprogress = function (event) {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = function () {
        var data = {};
        try {
          data = JSON.parse(xhr.responseText || '{}');
        } catch (error) {
          data = {};
        }
        if (xhr.status >= 200 && xhr.status < 300 && data.publicUrl) {
          resolve(data.publicUrl);
        } else {
          // The app explains itself in plain language — too large, not really an
          // image, link already used — so prefer its wording over a status code.
          reject(new Error(data.error || 'Upload failed (' + xhr.status + ').'));
        }
      };

      xhr.onerror = function () {
        reject(
          new Error(
            xhr.status === 0
              ? "Upload failed — we couldn't reach the server. Please check your connection and try again."
              : 'Upload failed (' + xhr.status + ').'
          )
        );
      };

      xhr.send(file);
    });
  }

  // ---- rendering ---------------------------------------------------------

  function buildSlot(itemState, index) {
    var frag = slotTemplate.content.cloneNode(true);
    var slot = frag.querySelector('[data-ud-slot]');
    var input = slot.querySelector('[data-ud-slot-input]');
    var pick = slot.querySelector('[data-ud-slot-pick]');
    var preview = slot.querySelector('[data-ud-slot-preview]');
    var img = slot.querySelector('[data-ud-slot-img]');
    var progress = slot.querySelector('[data-ud-slot-progress]');
    var bar = slot.querySelector('[data-ud-slot-bar]');
    var status = slot.querySelector('[data-ud-slot-status]');
    var remove = slot.querySelector('[data-ud-slot-remove]');

    /**
     * Which upload this slot is currently waiting for.
     *
     * Bumped on every action that changes the slot's mind — removing a photo,
     * picking a different one — so that a request already in flight can be
     * recognised as stale when it finally lands. Without it, the ✕ button only
     * looked like it cancelled: the request kept going and its late handler
     * wrote the removed photo straight back into the slot.
     *
     * The damage was invisible to everyone. The customer had already seen the
     * replacement confirmed on screen, the server had no way to tell an
     * abandoned object from a chosen one (both sit in this order's prefix and
     * both exist), and the moderator received the photo that had been deleted.
     * sections/upload-details.liquid says the quiet part out loud: a photo
     * attached to the wrong thing is worse than a missing one, because it looks
     * like an answer.
     */
    var uploadGeneration = 0;

    function reset() {
      uploadGeneration += 1;
      itemState.photos[index] = null;
      input.value = '';
      img.removeAttribute('src');
      hide(preview);
      hide(progress);
      show(pick);
      status.textContent = '';
      slot.classList.remove('ud-slot--failed', 'ud-slot--done');
      updateSubmitState();
    }

    pick.addEventListener('click', function () {
      input.click();
    });
    remove.addEventListener('click', reset);

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      if (!file) return;

      // Before the size check, not after: rejecting an oversized pick must also
      // retire whatever was still uploading, or the old one lands on top of the
      // "too large" message and the slot claims to hold a photo again.
      uploadGeneration += 1;
      var generation = uploadGeneration;

      // Checked here as well as on the server. The server's check is the one
      // that counts — it cuts the stream at the limit — but it can only do that
      // while the bytes are arriving, and the customer should find out now
      // rather than after a minute of uploading.
      if (config.maxPhotoBytes && file.size > config.maxPhotoBytes) {
        var limitMb = Math.round(config.maxPhotoBytes / (1024 * 1024));
        slot.classList.add('ud-slot--failed');
        show(preview);
        hide(pick);
        status.textContent =
          'That photo is too large (max ' + limitMb + ' MB). Please pick another.';
        itemState.photos[index] = null;
        updateSubmitState();
        return;
      }

      hide(pick);
      show(preview);
      slot.classList.remove('ud-slot--failed', 'ud-slot--done');
      img.src = URL.createObjectURL(file);
      show(progress);
      bar.style.width = '0%';
      status.textContent = 'Uploading...';
      itemState.photos[index] = null;
      updateSubmitState();

      uploadPhoto(file, itemState.lineItemId, function (percent) {
        bar.style.width = percent + '%';
      })
        .then(function (publicUrl) {
          // The slot has moved on — this photo was removed or replaced while it
          // was still uploading. Its result is not an answer to anything.
          if (generation !== uploadGeneration) return;
          itemState.photos[index] = publicUrl;
          hide(progress);
          slot.classList.add('ud-slot--done');
          status.textContent = 'Uploaded';
          updateSubmitState();
        })
        .catch(function (error) {
          // Same for a failure: reporting it would overwrite whatever the
          // customer has chosen since.
          if (generation !== uploadGeneration) return;
          hide(progress);
          slot.classList.add('ud-slot--failed');
          status.textContent = error.message || 'Upload failed.';
          itemState.photos[index] = null;
          updateSubmitState();
        });
    });

    return frag;
  }

  function buildField(itemState, field) {
    var frag = fieldTemplate.content.cloneNode(true);
    var label = frag.querySelector('[data-ud-field-label]');
    var input = frag.querySelector('[data-ud-field-input]');
    var id = 'ud-' + itemState.lineItemId + '-' + field.key;

    label.textContent = field.required ? field.label + ' *' : field.label;
    label.setAttribute('for', id);
    input.id = id;
    input.setAttribute('placeholder', field.label);
    if (config.maxValueChars) input.maxLength = config.maxValueChars;
    if (field.required) input.required = true;

    input.addEventListener('input', function () {
      input.classList.remove('ud-field__input--invalid');
      updateSubmitState();
    });

    itemState.values[field.key] = input;
    return frag;
  }

  function buildItem(item) {
    // `fields` is kept on the state, not looked up again at submit time: the
    // required-field check runs over `state`, and each canvas now has its own
    // list. Reading a request-wide list there would demand every canvas satisfy
    // every other canvas's required fields.
    var itemState = {
      lineItemId: item.lineItemId,
      photos: [],
      values: {},
      fields: item.fields || [],
    };

    var frag = itemTemplate.content.cloneNode(true);
    var thumb = frag.querySelector('[data-ud-item-thumb]');
    var name = frag.querySelector('[data-ud-item-name]');
    var sub = frag.querySelector('[data-ud-item-sub]');
    var photos = frag.querySelector('[data-ud-photos]');
    var photosLabel = frag.querySelector('[data-ud-photos-label]');
    var grid = frag.querySelector('[data-ud-photos-grid]');
    var fields = frag.querySelector('[data-ud-fields]');

    if (item.imageUrl) {
      var image = document.createElement('img');
      image.src = item.imageUrl;
      image.alt = item.productName || '';
      image.loading = 'lazy';
      thumb.appendChild(image);
    }
    name.textContent = item.productName || '';
    sub.textContent = [item.sku, item.size].filter(Boolean).join(' · ');

    // What is asked comes from the CANVAS, not the request. A request covering
    // three canvases can want two photos of one, a date from another and
    // nothing at all from the third — reading request-wide numbers here is what
    // made every canvas show the same boxes.
    var slots = item.photoSlots || 0;
    var itemFields = item.fields || [];

    // The moderator's note for this canvas, if they left one. Rendered above
    // the boxes it explains rather than with the request's message, which
    // introduces the whole email and says nothing about any one canvas.
    if (item.note) {
      var note = document.createElement('p');
      note.className = 'ud-item__note';
      note.textContent = item.note;
      sub.parentNode.appendChild(note);
    }

    // A request may ask for photos, values, or both. Zero photo slots is a
    // normal shape — the whole block stays hidden rather than showing an empty
    // row the customer would wonder about.
    if (slots > 0) {
      photosLabel.textContent =
        slots === 1 ? 'Add 1 photo' : 'Add up to ' + slots + ' photos';
      for (var i = 0; i < slots; i += 1) {
        itemState.photos.push(null);
        grid.appendChild(buildSlot(itemState, i));
      }
      show(photos);
    }

    if (itemFields.length) {
      itemFields.forEach(function (field) {
        fields.appendChild(buildField(itemState, field));
      });
      show(fields);
    }

    state.push(itemState);
    itemsEl.appendChild(frag);
  }

  // ---- submit ------------------------------------------------------------

  /** Everything the customer has actually provided, per canvas. */
  function collect() {
    return state.map(function (itemState) {
      var values = {};
      Object.keys(itemState.values).forEach(function (key) {
        var value = itemState.values[key].value.trim();
        if (value) values[key] = value;
      });
      return {
        lineItemId: itemState.lineItemId,
        photos: itemState.photos.filter(Boolean),
        values: values,
      };
    });
  }

  function hasAnything() {
    return collect().some(function (response) {
      return response.photos.length > 0 || Object.keys(response.values).length > 0;
    });
  }

  function missingRequired() {
    var missing = [];
    state.forEach(function (itemState) {
      itemState.fields.forEach(function (field) {
        if (!field.required) return;
        var input = itemState.values[field.key];
        if (input && !input.value.trim()) missing.push(input);
      });
    });
    return missing;
  }

  function uploadsInFlight() {
    return !!root.querySelector('[data-ud-slot-progress]:not([hidden])');
  }

  function updateSubmitState() {
    if (sending) return;
    submitEl.disabled = !hasAnything() || uploadsInFlight();
  }

  formEl.addEventListener('submit', function (event) {
    event.preventDefault();
    if (sending) return;

    hide(formErrorEl);

    var missing = missingRequired();
    if (missing.length) {
      missing.forEach(function (input) {
        input.classList.add('ud-field__input--invalid');
      });
      formErrorEl.textContent = 'Please fill in every field marked with *.';
      show(formErrorEl);
      missing[0].focus();
      return;
    }

    if (!hasAnything()) {
      formErrorEl.textContent = 'Please add at least one photo or answer before sending.';
      show(formErrorEl);
      return;
    }

    sending = true;
    submitEl.disabled = true;
    submitEl.textContent = 'Sending...';

    fetch(API_BASE + DETAILS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        Object.assign({}, identifiers(), { responses: collect() })
      ),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { status: res.status, ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        if (result.ok) {
          showOnly(successEl);
          return;
        }
        // 409 is not an error the customer can act on by retrying — the link is
        // spent or expired, and those have their own screens.
        if (result.status === 409) {
          showOnly(/expire/i.test(result.data.error || '') ? expiredEl : submittedEl);
          return;
        }
        throw new Error(result.data.error || 'Something went wrong. Please try again.');
      })
      .catch(function (error) {
        sending = false;
        submitEl.disabled = false;
        submitEl.textContent = submitEl.getAttribute('data-original-label');
        formErrorEl.textContent = error.message;
        show(formErrorEl);
      });
  });

  // ---- boot --------------------------------------------------------------

  if (!API_BASE || !orderId || !requestId || !token) {
    fail();
    return;
  }

  submitEl.setAttribute('data-original-label', submitEl.textContent.trim());

  fetch(API_BASE + DETAILS_ENDPOINT + query())
    .then(function (res) {
      return res.json().then(function (data) {
        return { status: res.status, ok: res.ok, data: data };
      });
    })
    .then(function (result) {
      // 401/404 mean the link itself is not valid — a different message from a
      // link that was valid and has been used.
      if (!result.ok) {
        fail();
        return;
      }

      config = result.data;

      if (orderNumberEl && config.orderNumber) {
        orderNumberEl.textContent = 'Order ' + config.orderNumber;
        show(orderNumberEl);
      }

      // Both arrive in a normal 200 — they are states, not failures, and must
      // be checked before anything is rendered.
      if (config.submitted) {
        showOnly(submittedEl);
        return;
      }
      if (config.expired) {
        showOnly(expiredEl);
        return;
      }

      messageEl.textContent = config.message || '';
      (config.items || []).forEach(buildItem);

      if (!state.length) {
        fail();
        return;
      }

      showOnly(formEl);
      updateSubmitState();
    })
    .catch(function () {
      fail();
    });
});
